import type { ReactNode } from "react";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

import { useCommentsSection } from "@/react/components/CommentsCompass/Provider/SectionContext";
import type { BasicCommunityMember } from "@/react/types/CommunityMember";

interface ThreadContextValue {
  isReplyFormOpen: boolean;
  openReplyForm: (params: { communityMember: BasicCommunityMember }) => void;
}

const ThreadContext = createContext<ThreadContextValue | null>(null);
ThreadContext.displayName = "ThreadContext";

interface CommentsThreadProviderProps {
  children: ReactNode;
  commentId: number;
  defaultReplyFormOpen: boolean;
}

export const CommentsThreadProvider = ({
  children,
  commentId,
  defaultReplyFormOpen,
}: CommentsThreadProviderProps) => {
  const { requestComposerFocus } = useCommentsSection();
  const [isReplyFormOpen, setIsReplyFormOpen] = useState(defaultReplyFormOpen);

  const openReplyForm = useCallback(
    ({ communityMember }: { communityMember: BasicCommunityMember }) => {
      setIsReplyFormOpen(true);
      requestComposerFocus({
        kind: "reply",
        commentId,
        mentionCommunityMember: communityMember,
      });
    },
    [commentId, requestComposerFocus],
  );

  const value = useMemo<ThreadContextValue>(
    () => ({ isReplyFormOpen, openReplyForm }),
    [isReplyFormOpen, openReplyForm],
  );

  return (
    <ThreadContext.Provider value={value}>{children}</ThreadContext.Provider>
  );
};

export const useCommentsThread = (): ThreadContextValue => {
  const value = useContext(ThreadContext);
  if (!value) {
    throw new Error(
      "useCommentsThread must be used inside a CommentsThreadProvider. Threads are scoped " +
        "to a top-level Comment — render this component as a descendant of " +
        "a top-level Comment in CommentsCompass.",
    );
  }
  return value;
};
